import { CHANNEL_INFO } from './marketplace';
import { productBarcodeKind } from './barcodes';
import { code128Modules } from './code128';
import type { PostalAddress } from './common';

/** Input of our own carrier label (seller or warehouse contract; marketplaces send theirs). */
export interface ShippingLabelInput {
  readonly size: '100x150' | '100x100';
  readonly carrierName: string;
  /** Printed as Code 128: letters, digits and dashes; null: no barcode (the carrier puts its own on at pickup) */
  readonly trackingNumber: string | null;
  readonly orderNumber: string;
  readonly sellerCode: string;
  readonly sender: { readonly name: string; readonly address: string; readonly phone?: string | null };
  readonly recipient: PostalAddress;
  /** printed only when given: the estimate is often wrong, so it is added by hand when printing (withDesi) */
  readonly desi?: number | null;
  readonly parcel: { readonly index: number; readonly count: number };
  readonly note?: string | null;
  /** Aras without an integration: the barcode is the parcel's müşteri özel kodu, printed as "MÖK: …" */
  readonly mok?: string | null;
}

const DOTS_PER_MM = 8; // 203 dpi thermal printers

/** Field data for ^FH\: the characters ZPL treats as commands become hex escapes. */
export function zplEscape(text: string): string {
  return text.replace(/[\\^~]/g, (c) => `\\${c.charCodeAt(0).toString(16).toUpperCase().padStart(2, '0')}`);
}

/** The empty place on our labels where the desi goes when it is added at print time (a ZPL comment prints nothing). */
const DESI_SLOT = '^FXDESI^FS';

// --- keeping what we draw inside the label ----------------------------------------------------

/** Quiet zone a scanner needs on each side of a linear barcode, in modules (Code 128: 10). */
export const QUIET_ZONE_MODULES = 10;

/**
 * Module width (dots) and left edge of a linear barcode `modules` modules wide, centred in the
 * span [left, left + span): the widest module from `widest` down that leaves the quiet zone on
 * both sides inside the span; 1 dot only when nothing wider fits.
 */
export function barcodeFit(modules: number, left: number, span: number, widest = 3): { readonly module: number; readonly x: number } {
  let module = Math.max(1, Math.floor(widest));
  while (module > 1 && (modules + 2 * QUIET_ZONE_MODULES) * module > span) module--;
  return { module, x: left + Math.max(0, Math.round((span - modules * module) / 2)) };
}

/**
 * Advances in thousandths of an em of the widest face a label's font 0 (^A0, CG Triumvirate Bold
 * Condensed) is drawn in: Arial Bold, the browser's fallback when the computer printing through
 * its driver has no Arial Narrow. Accented letters take their base letter's; anything else an em.
 */
const ADVANCES = new Map<string, number>(
  (
    [
      ["'", 238],
      [' ,./\\|ijlIı·‘’‚', 280],
      ['!()-:;[]`ft', 333],
      ['*{}r', 389],
      ['"', 474],
      ['z“”„', 500],
      ['#$0123456789_aceksvxyJ–', 556],
      ['+<=>^~×', 584],
      ['?FLTZbdghnopqu', 611],
      ['EPSVXY', 667],
      ['&ABCDHKNRU', 722],
      ['GOQw', 778],
      ['M', 833],
      ['%m', 889],
      ['W', 944],
      ['@', 975],
    ] as const
  ).flatMap(([chars, advance]) => [...chars].map((ch) => [ch, advance] as const)),
);

/** Advance of a character in ems of the font's width, 2 % on the wide side. */
function glyphEm(ch: string): number {
  const advance = ADVANCES.get(ch) ?? ADVANCES.get(ch.normalize('NFD')[0] ?? '') ?? 1000;
  return (advance * 1.02) / 1000;
}

/** Width in dots a text takes in font 0 whose width parameter is `fontWidth` (^A0N,h,w: w), estimated on the wide side. */
export function zplTextWidth(text: string, fontWidth: number): number {
  let em = 0;
  for (const ch of text) em += glyphEm(ch);
  return em * fontWidth;
}

/**
 * A field block's lines as a printer breaks them, at spaces (a soft break takes one space away); a
 * word longer than a line is cut where it must (`hard`: the break after that line is such a cut),
 * so that it breaks there on the printer too once the pieces are set apart.
 */
function wrapLines(text: string, maxWidth: number, fontWidth: number): { readonly lines: string[]; readonly hard: boolean[] } {
  const fits = (s: string) => zplTextWidth(s, fontWidth) <= maxWidth;
  const lines: string[] = [];
  const hard: boolean[] = [];
  let line: string | null = null;
  for (const word of text.split(' ')) {
    const next: string = line === null ? word : `${line} ${word}`;
    if (fits(next)) {
      line = next;
      continue;
    }
    if (line !== null) {
      lines.push(line);
      hard.push(false);
    }
    let rest = [...word];
    while (rest.length > 1 && !fits(rest.join(''))) {
      let n = 1;
      while (n < rest.length && fits(rest.slice(0, n + 1).join(''))) n++;
      lines.push(rest.slice(0, n).join(''));
      hard.push(true);
      rest = rest.slice(n);
    }
    line = rest.join('');
  }
  if (line !== null) {
    lines.push(line);
    hard.push(false);
  }
  return { lines, hard };
}

/** The lines a field block `maxWidth` dots wide breaks a text into, at most (font 0, width parameter `fontWidth`). */
export function zplBlockLines(text: string, maxWidth: number, fontWidth: number): string[] {
  return wrapLines(text, maxWidth, fontWidth).lines;
}

interface FittedText {
  readonly text: string;
  readonly size: number;
  readonly lines: number;
}

/**
 * A value set into a field block `maxWidth` dots wide: at `size` when its lines fit the block,
 * else at the largest smaller size down to `min` at which they do, else at `min` with what does
 * not fit cut off behind "…". `lines` gives the block's lines at a size.
 */
function fitText(value: string, maxWidth: number, size: number, min: number, lines: (size: number) => number, aspect: number): FittedText {
  // a printer ignores line breaks in field data: the words would run together. No block of ours
  // holds 1000 characters even in its smallest type: what lies beyond is cut anyway
  const text = [...value.replace(/[\r\n\t]+/g, ' ')].slice(0, 1000).join('');
  const fontWidth = (s: number) => Math.max(1, Math.round(s * aspect));
  for (let s = size; s >= min; s--) {
    const wrapped = wrapLines(text, maxWidth, fontWidth(s));
    // a word cut into pieces goes out with the pieces set apart, so the printer breaks it there too
    if (wrapped.lines.length <= lines(s)) return { text: wrapped.hard.includes(true) ? wrapped.lines.join(' ') : text, size: s, lines: lines(s) };
  }
  // the lines before the last as they break; the last one takes the rest of the text, cut to fit with "…"
  const n = lines(min);
  const { lines: all, hard } = wrapLines(text, maxWidth, fontWidth(min));
  const kept = all.slice(0, n - 1);
  const consumed = kept.reduce((sum, l, i) => sum + l.length + (hard[i] ? 0 : 1), 0);
  const rest = [...text.slice(consumed)];
  while (rest.length > 0 && zplTextWidth(`${rest.join('').trimEnd()}…`, fontWidth(min)) > maxWidth) rest.pop();
  return { text: [...kept, `${rest.join('').trimEnd()}…`].join(' '), size: min, lines: n };
}

type Justify = 'L' | 'C' | 'R';

interface TextFieldOptions {
  /** ^A0 height in dots */
  readonly size: number;
  /** the smallest size a long value is set in before it is cut (default: no smaller) */
  readonly min?: number;
  /** the block's lines (default 1) … */
  readonly lines?: number;
  /** … or the height in dots it may take, the lines being what fits at the size chosen */
  readonly height?: number;
  readonly spacing?: number;
  readonly justify?: Justify;
  /** ^A0 width over height */
  readonly aspect?: number;
}

/**
 * A text field in a block `width` dots wide from x: ^FB holds every line to the block, and the
 * value is set at a size whose lines all fit it (a printer prints what does not fit over the
 * block's last line), so no value draws past the block, nor past the label's edge.
 */
function textField(x: number, y: number, width: number, value: string, o: TextFieldOptions): string {
  const spacing = o.spacing ?? 0;
  const aspect = o.aspect ?? 1;
  const lines = (size: number) => (o.height === undefined ? (o.lines ?? 1) : Math.max(1, Math.floor((o.height + spacing) / (size + spacing))));
  const fit = fitText(value, width, o.size, Math.min(o.size, o.min ?? o.size), lines, aspect);
  return `^FO${x},${y}^FB${width},${fit.lines},${spacing},${o.justify ?? 'L'}^A0N,${fit.size},${Math.max(1, Math.round(fit.size * aspect))}^FH\\^FD${zplEscape(fit.text)}^FS`;
}

/**
 * 100×150 mm (or compact 100×100 mm) label in ZPL II: carrier and parcel n/m, sender,
 * recipient in large type, the tracking number as Code 128 with the code under it, order /
 * seller footer, and the desi when given (else an empty slot that withDesi fills). Every text
 * is a block within the margins, set smaller when long; the barcode keeps its quiet zones.
 * ^CI28 switches the printer to UTF-8 so Turkish letters print as they are.
 */
export function shippingLabelZpl(input: ShippingLabelInput): string {
  const code = input.trackingNumber;
  if (code !== null && !/^[A-Z0-9-]{4,40}$/.test(code)) {
    throw new RangeError(`tracking number not printable as Code 128: ${code}`);
  }
  const width = 100 * DOTS_PER_MM;
  const height = (input.size === '100x150' ? 150 : 100) * DOTS_PER_MM;
  const y = (mmFromTop: number) => Math.round((mmFromTop * height) / 150); // layout drawn for 150 mm, scaled
  const font = (size: number) => Math.round((size * height) / 1200);
  const left = 30;
  const inner = width - 2 * left;
  // one line, across the margins unless given its own place; a long value down to 2/3 of the size, then cut
  const line = (top: number, size: number, value: string, at: { readonly x?: number; readonly width?: number; readonly justify?: Justify } = {}) =>
    textField(at.x ?? left, y(top), at.width ?? inner, value, { size: font(size), min: font(Math.round((size * 2) / 3)), justify: at.justify });
  // as many lines as fit between top and bottom (mm), smaller ones when the value is long
  const block = (top: number, bottom: number, size: number, min: number, value: string) =>
    textField(left, y(top), inner, value, { size: font(size), min: font(min), height: y(bottom) - y(top), spacing: 4 });
  const rule = (top: number) => `^FO${left},${y(top)}^GB${inner},3,3^FS`;
  // Code 128 in set B (">:", what a printer draws for ^BC without a mode, so its width is exact),
  // centred at the widest module that keeps its quiet zones inside the margins
  const bars = code ? barcodeFit(code128Modules(code, 'B').length, left, inner) : null;

  const r = input.recipient;
  const street = [r.line1, r.line2, r.neighborhood].filter((p): p is string => Boolean(p)).join(', ');
  return [
    '^XA',
    '^CI28',
    `^PW${width}`,
    `^LL${height}`,
    line(4, 52, input.carrierName, { width: inner - 210 }),
    line(5, 40, `${input.parcel.index}/${input.parcel.count}`, { x: width - left - 200, width: 200, justify: 'R' }),
    rule(12),
    // a company's full title takes two lines
    block(13.5, 20, 22, 16, `Gönderen: ${input.sender.name}`),
    block(20, 26, 19, 15, input.sender.address + (input.sender.phone ? `  Tel: ${input.sender.phone}` : '')),
    rule(26),
    line(28, 24, 'ALICI'),
    // a long name smaller, a very long one on two lines
    block(32, 38, 42, 22, r.fullName),
    block(38, 51, 30, 22, street),
    line(51, 36, `${r.district} / ${r.city}${r.postalCode ? `  ${r.postalCode}` : ''}`),
    r.phone ? line(57, 28, `Tel: ${r.phone}`) : '',
    rule(63),
    code && bars ? `^FO${bars.x},${y(66)}^BY${bars.module}^BCN,${y(26)},N,N,N^FD>:${code}^FS` : '',
    // the code under the bars in type that reads at any module (the printer's own line shrinks with the bars)
    code ? line(92.8, 30, code, { justify: 'C' }) : '',
    // for the branch clerk who types it into the MÖK field
    input.mok ? line(97.2, 24, `MÖK: ${input.mok}`, { justify: 'R' }) : '',
    rule(102),
    line(105, 30, `Sipariş: ${input.orderNumber}`),
    line(111, 30, `Satıcı: ${input.sellerCode}`, { width: 430 }),
    input.desi === undefined || input.desi === null
      ? `^FO480,${y(111)}^FB${width - left - 480},1,0,L^A0N,${font(30)},${font(30)}${DESI_SLOT}`
      : line(111, 30, `Desi: ${input.desi}`, { x: 480, width: width - left - 480 }),
    input.note ? block(118, 146, 24, 18, input.note) : '',
    '^XZ',
  ]
    .filter(Boolean)
    .join('\n');
}

/** A product line on a marketplace parcel's label. */
export interface MarketplaceLabelLine {
  readonly name: string;
  readonly quantity: number;
  readonly color: string | null;
  readonly size: string | null;
  readonly barcode: string | null;
  readonly sku: string | null;
}

/** A parcel the marketplace's contracted carrier takes, on our label (when the marketplace will not draw it). */
export interface MarketplaceLabelInput {
  /** "trendyol": the site is trendyol.com, the contract Trendyol's */
  readonly channel: string;
  readonly carrierName: string;
  /** Printed as Code 128 set B, as the marketplace prints it */
  readonly trackingNumber: string;
  readonly orderNumber: string;
  readonly recipient: PostalAddress;
  readonly lines: readonly MarketplaceLabelLine[];
  readonly parcel: { readonly index: number; readonly count: number };
}

/**
 * 100×150 mm label with the marketplace label's sections and words (Trendyol's: the note to the
 * carrier, the marketplace and the carrier, Kargo Barkodu, Alıcı Bilgileri, Ürün Bilgileri), set one
 * under the other for the roll. The desi slot stays empty unless filled when printing (withDesi).
 */
export function marketplaceLabelZpl(input: MarketplaceLabelInput): string {
  if (!/^[A-Z0-9-]{4,40}$/.test(input.trackingNumber)) {
    throw new RangeError(`tracking number not printable as Code 128: ${input.trackingNumber}`);
  }
  const W = 100 * DOTS_PER_MM;
  const known = (CHANNEL_INFO as Readonly<Record<string, { readonly name: string; readonly site: string }>>)[input.channel];
  const site = known?.site || `${input.channel.toLocaleLowerCase('tr-TR')}.com`;
  const name = known?.name ?? `${input.channel.charAt(0).toLocaleUpperCase('tr-TR')}${input.channel.slice(1)}`;
  // the marketplace in big letters as its label writes it ("trendyol"); a long name in smaller ones
  const brand = name.toLocaleLowerCase('tr-TR');
  // every text a block of its own place: a long value is set smaller (to 70 %), then cut
  const text = (x: number, y: number, width: number, size: number, value: string, justify: Justify = 'L') =>
    textField(x, y, width, value, { size, min: Math.round(size * 0.7), justify });
  const block = (x: number, y: number, width: number, lines: number, size: number, value: string) =>
    textField(x, y, width, value, { size, min: Math.round(size * 0.8), lines, spacing: 2 });
  const box = (y: number, h: number) => `^FO24,${y}^GB${W - 48},${h},2,B,1^FS`;

  // the barcode as wide as fits: set B, 11 modules a character plus start, check and stop, with its
  // quiet zones inside the box (24 … 776, lines 2 dots thick)
  const bars = barcodeFit(code128Modules(input.trackingNumber, 'B').length, 26, W - 52);

  const r = input.recipient;
  const street = [r.line1, r.line2, r.neighborhood].filter((p): p is string => Boolean(p)).join(' ');
  const row = (y: number, label: string, value: string) => [text(44, y, 180, 26, label), text(228, y, 24, 26, ':'), text(256, y, W - 300, 26, value)];

  const shown = input.lines.length <= 2 ? input.lines : input.lines.slice(0, 1);
  const products = shown.flatMap((l, i) => {
    const y = 980 + i * 100;
    const columns: [string, string][] = [
      ['Adet', `${l.quantity} Adet`],
      ['Renk', l.color || '-'],
      ['Beden', l.size || '-'],
      ['Barkod', l.barcode || '-'],
      ['Stok Kodu', l.sku || '-'],
    ];
    return [
      `^FO44,${y + 4}^GC52,2,B^FS`,
      text(44, y + 18, 52, 24, String(i + 1), 'C'),
      block(112, y, W - 150, 2, 22, l.name),
      // a column 120 dots wide, 8 to the next
      ...columns.flatMap(([label, value], c) => [text(112 + c * 128, y + 50, 120, 18, label), text(112 + c * 128, y + 72, 120, 20, value)]),
    ];
  });
  const more = input.lines.length - shown.length;

  return [
    '^XA',
    '^CI28',
    `^PW${W}`,
    '^LL1200',
    // the note to the carrier
    `^FO24,24^GB${W - 48},92,2,B,1^FS`,
    block(44, 38, W - 88, 2, 22, `Kargo şirketinin dikkatine, bu bir ${site} gönderisidir. ${name} anlaşmasına uygun işlem yapabilirsiniz.`),
    // the marketplace and the carrier, side by side
    text(30, 138, 380, brand.length > 8 ? 60 : 92, brand),
    textField(420, 160, W - 450, input.carrierName, { size: 52, min: 28, justify: 'R' }),
    // Kargo Barkodu
    box(250, 370),
    text(44, 268, 340, 32, 'Kargo Barkodu'),
    input.parcel.count > 1 ? text(400, 268, W - 444, 30, `Koli ${input.parcel.index}/${input.parcel.count}`, 'R') : '',
    `^FO${bars.x},322^BY${bars.module}^BCN,210,Y,N,N^FD>:${input.trackingNumber}^FS`,
    `^FO560,578^FB200,1,0,L^A0N,26,26${DESI_SLOT}`,
    // Alıcı Bilgileri
    box(636, 270),
    text(44, 652, W - 88, 32, 'Alıcı Bilgileri'),
    ...row(706, 'Sipariş No', input.orderNumber),
    ...row(746, 'Ad-Soyad', r.fullName),
    text(44, 786, 180, 26, 'Adres'),
    text(228, 786, 24, 26, ':'),
    textField(256, 786, W - 300, street, { size: 24, min: 18, height: 64, spacing: 2 }),
    text(256, 852, W - 300, 28, `${r.district}/${r.city}`),
    // Ürün Bilgileri
    box(922, 254),
    text(44, 936, W - 88, 32, 'Ürün Bilgileri'),
    ...products,
    more > 0 ? text(112, 1090, W - 150, 22, `+${more} ürün daha`) : '',
    '^XZ',
  ]
    .filter(Boolean)
    .join('\n');
}

/** Whether a label is one of ours with room for the desi (marketplace labels are printed as they come). */
/** Labels in a ZPL document: one ^XA … ^XZ block each (a label of N boxes holds N). */
export function zplLabelCount(zpl: string): number {
  return (zpl.match(/\^XA/g) ?? []).length;
}

export function hasDesiSlot(zpl: string): boolean {
  return zpl.includes(DESI_SLOT);
}

/** Our label with the desi written into its slot, on every parcel's label; "3,5" desi prints as typed. */
export function withDesi(zpl: string, desi: string): string {
  return zpl.split(DESI_SLOT).join(`^FH\\^FD${zplEscape(`Desi: ${desi}`)}^FS`);
}

/** Label rolls thermal printers are usually loaded with, in millimetres (width × height). */
export const LABEL_SIZE_PRESETS = ['100x150', '100x100', '100x50', '80x50', '60x40', '50x30', '40x30'] as const;

/** A label size in millimetres, "100x150": any roll between 25–120 mm wide and 15–200 mm high. */
export type LabelSizeText = string;

export interface LabelSize {
  readonly width: number;
  readonly height: number;
}

/** "100x150" (or "100×150") → 100 × 150 mm; null for anything a 4-inch thermal printer cannot take. */
export function parseLabelSize(text: LabelSizeText): LabelSize | null {
  const m = /^(\d{2,3})\s*[x×]\s*(\d{2,3})$/i.exec(text.trim());
  if (!m) return null;
  const width = Number(m[1]);
  const height = Number(m[2]);
  return width >= 25 && width <= 120 && height >= 15 && height <= 200 ? { width, height } : null;
}

function labelDots(text: LabelSizeText): { readonly width: number; readonly height: number } {
  const size = parseLabelSize(text);
  if (!size) throw new RangeError(`label size not supported: ${text}`);
  return { width: size.width * DOTS_PER_MM, height: size.height * DOTS_PER_MM };
}

export interface LocationLabelInput {
  /** "A-03-2-1", printed large */
  readonly code: string;
  /** "LA030201", printed as Code 128 */
  readonly barcode: string;
  /** label roll, "100x50" */
  readonly size: LabelSizeText;
  /** small print under the barcode, e.g. the warehouse code and the bin type */
  readonly caption?: string | null;
}

/**
 * A bin label at any roll size: the code as large as the width allows, the barcode the pickers
 * scan (Code 128, the printer picks the subsets: ^BC mode A) and a caption, each line centred
 * across the roll and the block centred on tall rolls.
 */
export function locationLabelZpl(input: LocationLabelInput): string {
  if (!/^[A-Z0-9]{3,40}$/.test(input.barcode)) throw new RangeError(`location barcode not printable: ${input.barcode}`);
  const { width, height } = labelDots(input.size);
  const margin = Math.round(Math.max(16, Math.min(width, height) * 0.06));
  const inner = width - 2 * margin;
  // A0 glyphs are about 0.56 of their height wide; never wider than the line holds
  const codeHeight = Math.max(
    1,
    Math.round(Math.min(height * 0.26, inner / (Math.max(4, input.code.length) * 0.56), Math.floor(inner / Math.max(0.01, zplTextWidth(input.code, 1))))),
  );
  const captionHeight = Math.round(Math.max(18, Math.min(36, height * 0.07)));
  const gap = Math.round(margin * 0.6);
  // the printer picks the subsets (^BC mode A) as code128Modules does; the quiet zones stay inside the margins
  const bars = barcodeFit(code128Modules(input.barcode).length, margin, inner, 4);
  const barHeight = Math.round(Math.max(40, Math.min(height - 2 * margin - codeHeight - captionHeight - 2 * gap, inner * 0.5)));
  const top = Math.round(Math.max(margin, (height - (codeHeight + barHeight + captionHeight + 2 * gap)) / 2));
  const caption = [input.barcode, input.caption].filter(Boolean).join('  ·  ');
  return [
    '^XA',
    '^CI28',
    `^PW${width}`,
    `^LL${height}`,
    textField(margin, top, inner, input.code, { size: codeHeight, justify: 'C' }),
    `^FO${bars.x},${top + codeHeight + gap}^BY${bars.module},3,${barHeight}^BCN,${barHeight},N,N,N,A^FD${input.barcode}^FS`,
    textField(margin, top + codeHeight + barHeight + 2 * gap, inner, caption, { size: captionHeight, min: Math.min(captionHeight, 14), justify: 'C' }),
    '^XZ',
  ].join('\n');
}

export interface ProductLabelInput {
  readonly sku: string;
  readonly name: string;
  /** printed as EAN-13 when it is a valid GTIN-13, otherwise as Code 128 */
  readonly barcode: string;
  /** label roll, "50x30" */
  readonly size: LabelSizeText;
  readonly lotNo?: string | null;
  /** YYYY-MM-DD, printed as SKT dd.mm.yyyy */
  readonly expiryDate?: string | null;
  readonly sellerCode?: string | null;
  /** a carton's barcode: one scan counts this many units, printed as "KOLİ · 12 ADET" (1 or none: the unit's own) */
  readonly unitsPerScan?: number | null;
  readonly copies?: number;
}

/** ^PQ prints at most this many copies of one product label. */
export const PRODUCT_LABEL_MAX_COPIES = 500;

/** Separator between the parts of a label's line. */
const DOT = '  ·  ';

/**
 * "SKU  ·  SELLER" as the label's last line holds it at width `fontWidth`: when the two do not
 * fit, the SKU is cut behind "…" and the seller code stays whole (it says whose goods they are).
 */
export function skuAndSeller(sku: string, sellerCode: string | null | undefined, maxWidth: number, fontWidth: number): string {
  const line = (s: string) => (sellerCode ? `${s}${DOT}${sellerCode}` : s);
  if (zplTextWidth(line(sku), fontWidth) <= maxWidth) return line(sku);
  const kept = [...sku];
  while (kept.length > 1 && zplTextWidth(line(`${kept.join('')}…`), fontWidth) > maxWidth) kept.pop();
  return line(`${kept.join('')}…`);
}

/**
 * A product label for goods that arrive without a barcode (or with an unreadable one), or one of
 * the product's barcodes printed again ("Barkodu bas"): the name, the barcode the packers scan
 * (EAN-13 for a valid GTIN-13, else Code 128, so a scan reads back exactly the code saved), and
 * SKU with the seller code, every line centred. A carton's code, a lot and an SKT go on a line of
 * their own above that, so none of them pushes the SKU or the seller code off. Drawn for 50×30 mm
 * and scaled to the roll; on tall rolls the block sits in the middle.
 */
export function productLabelZpl(input: ProductLabelInput): string {
  if (!/^[!-~]{3,64}$/.test(input.barcode)) throw new RangeError(`barcode not printable: ${input.barcode}`);
  const { width, height } = labelDots(input.size);
  const k = Math.min(width / 400, height / 240);
  const px = (n: number) => Math.max(1, Math.round(n * k));
  const margin = px(14);
  const inner = width - 2 * margin;
  const ean = productBarcodeKind(input.barcode) === 'ean13';
  // Code 128 in ^BC mode A: the printer picks the subsets, as code128Modules does; quiet zones inside the margins
  const { module, x: barX } = barcodeFit(ean ? 95 : code128Modules(input.barcode).length, margin, inner, 4);
  const expiry = input.expiryDate ? `SKT ${input.expiryDate.slice(8, 10)}.${input.expiryDate.slice(5, 7)}.${input.expiryDate.slice(0, 4)}` : null;
  const carton = input.unitsPerScan && input.unitsPerScan > 1 ? `KOLİ · ${Math.floor(input.unitsPerScan)} ADET` : null;
  const meta = [carton, input.lotNo ? `LOT ${input.lotNo}` : null, expiry].filter(Boolean).join(DOT);
  // the bars give that line room under their human readable line
  const barHeight = px(meta ? 68 : 78);
  // the 50×30 layout is px(240) high: centre it on rolls that are taller
  const top = Math.max(0, Math.round((height - px(240)) / 2));
  const footerTop = top + px(240) - px(30);
  const footerAspect = px(20) / px(22);
  const footer = skuAndSeller(input.sku, input.sellerCode, inner, Math.max(1, Math.round(px(16) * footerAspect)));
  return [
    '^XA',
    '^CI28',
    `^PW${width}`,
    `^LL${height}`,
    // a long name smaller (to 3/4), then cut on its second line
    textField(margin, top + px(10), inner, input.name, { size: px(24), min: px(18), lines: 2, spacing: 2, justify: 'C', aspect: px(22) / px(24) }),
    ean
      ? `^FO${barX},${top + px(66)}^BY${module}^BEN,${barHeight},Y,N^FD${input.barcode.slice(0, 12)}^FS`
      : `^FO${barX},${top + px(66)}^BY${module}^BCN,${barHeight},Y,N,N,A^FD${input.barcode}^FS`,
    meta ? textField(margin, top + px(178), inner, meta, { size: px(18), min: px(14), justify: 'C', aspect: px(16) / px(18) }) : '',
    textField(margin, footerTop, inner, footer, { size: px(22), min: px(16), justify: 'C', aspect: footerAspect }),
    input.copies && input.copies > 1 ? `^PQ${Math.min(PRODUCT_LABEL_MAX_COPIES, Math.floor(input.copies))}` : '',
    '^XZ',
  ]
    .filter(Boolean)
    .join('\n');
}

/** Product labels sent to a packing desk's printer computer (POST /api/wms/stations/:code/product-labels). */
export interface DeskProductLabelRequest {
  /** the roll in the desk's printer, "100x150" */
  readonly size: LabelSizeText;
  readonly labels: readonly {
    readonly productId: string;
    /** one of the product's barcodes, or its SKU when it has none */
    readonly barcode: string;
    readonly copies: number;
    readonly lotNo?: string | null;
    readonly expiryDate?: string | null;
  }[];
}

/** What went to the desk: labels in all (copies counted), and whether its printer computer was heard from lately. */
export interface DeskProductLabelsQueued {
  readonly station: string;
  readonly labels: number;
  readonly online: boolean;
}

/** A product label waiting for the desk's printer computer. */
export interface DeskProductLabelJob {
  readonly id: string;
  readonly sku: string;
  readonly copies: number;
  readonly zpl: string;
}

/**
 * A calibration label for a new printer: a frame on the edge, millimetre ticks, a Code 128 and a
 * QR code to scan. Printed at true size, the frame touches every edge and 10 ticks are 1 cm.
 */
export function testLabelZpl(size: LabelSizeText, stationCode: string): string {
  const { width, height } = labelDots(size);
  const w = width / DOTS_PER_MM;
  const h = height / DOTS_PER_MM;
  const big = h >= 100;
  const ticks: string[] = [];
  for (let mm = 10; mm < w; mm += 10) ticks.push(`^FO${mm * DOTS_PER_MM},0^GB2,${mm % 50 === 0 ? 40 : 24},2^FS`);
  for (let mm = 10; mm < h; mm += 10) ticks.push(`^FO0,${mm * DOTS_PER_MM}^GB${mm % 50 === 0 ? 40 : 24},2,2^FS`);
  // from x = 60 to the frame, the barcode's quiet zone included (a narrow roll takes thinner bars)
  const span = width - 4 - 60;
  const module = barcodeFit(code128Modules('TEST-1234567890', 'B').length, 60, span, big ? 3 : 2).module;
  return [
    '^XA',
    '^CI28',
    `^PW${width}`,
    `^LL${height}`,
    `^FO0,0^GB${width},${height},4^FS`,
    ...ticks,
    textField(60, big ? 70 : 34, span, 'DENEME ETİKETİ', { size: big ? 56 : 28, min: big ? 36 : 18 }),
    textField(60, big ? 140 : 70, span, `Masa ${stationCode} · ${w}×${h} mm`, { size: big ? 30 : 20, min: big ? 20 : 14 }),
    `^FO60,${big ? 200 : 100}^BY${module}^BCN,${big ? 140 : 70},Y,N,N^FDTEST-1234567890^FS`,
    big ? `^FO60,420^BQN,2,6^FDQA,Pronto Depo deneme etiketi^FS` : '',
    big ? textField(60, height - 120, width - 120, 'Çerçeve kenarlara değiyor ve 10 çizgi 1 cm ise ölçek doğru. Barkodu ve QR kodu okutup deneyin.', { size: 26, min: 20, lines: 3, spacing: 4 }) : '',
    '^XZ',
  ]
    .filter(Boolean)
    .join('\n');
}
